/**
 * Utility Functions for gemengApp modules
 * Common utilities and helpers shared across modules
 */

/**
 * Display error message under a form field
 * @param {Element} field - The form field element
 * @param {Array|string} messages - Error message(s) to display
 */
export function displayErrorUnderField(field, messages) {
    if (!field) return;
    
    // Remove existing error
    const existingError = field.parentNode.querySelector('.flatpickr-overlap-error');
    if (existingError) {
        existingError.remove();
    }
    
    // Create error element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'flatpickr-overlap-error alert alert-danger';
    errorDiv.style.fontSize = '12px';
    errorDiv.style.padding = '20px 30px';
    
    // Create list element
    const list = document.createElement('ul');
    list.style.margin = '0';
    list.style.padding = '0';
    list.style.listStyleType = 'none';
    
    // Add messages to list
    const messageArray = Array.isArray(messages) ? messages : [messages];
    messageArray.forEach(msg => {
        const item = document.createElement('li');
        item.textContent = msg;
        list.appendChild(item);
    });
    
    errorDiv.appendChild(list);
    field.parentNode.appendChild(errorDiv);
}

/**
 * Clear error messages for a specific field
 * @param {Element} field - The form field element
 */
export function clearFieldErrors(field) {
    if (!field) return;
    
    const errorElement = field.parentNode?.querySelector('.flatpickr-overlap-error');
    if (errorElement) {
        errorElement.remove();
    }
    
    field.classList.remove('is-invalid');
    
    // Remove individual error feedback
    const errorMsg = field.parentNode?.querySelector('.invalid-feedback');
    if (errorMsg) {
        errorMsg.remove();
    }
}

/**
 * Show loading indicator for flatpickr fields
 */
export function showFlatpickrLoading() {
    const dateFields = document.querySelectorAll('input[type="text"][name*="Date"], input[type="text"][name*="Time"]');
    dateFields.forEach(field => {
        if (!field.closest('.loading-flatpickr')) {
            const wrapper = document.createElement('div');
            wrapper.className = 'loading-flatpickr position-relative';
            field.parentNode.insertBefore(wrapper, field);
            wrapper.appendChild(field);

            const loader = document.createElement('div');
            loader.className = 'flatpickr-loader position-absolute';
            loader.style.cssText = `
                top: 50%; right: 10px; transform: translateY(-50%);
                width: 16px; height: 16px;
                border: 2px solid #f3f3f3;
                border-top: 2px solid #007bff;
                border-radius: 50%;
                animation: flatpickr-spin 1s linear infinite;
                z-index: 10;
            `;
            wrapper.appendChild(loader);

            field.style.paddingRight = '35px';
            field.disabled = true;
        }
    });
}

/**
 * Hide loading indicator for flatpickr fields
 */
export function hideFlatpickrLoading() {
    document.querySelectorAll('.flatpickr-loader').forEach(loader => loader.remove());
    document.querySelectorAll('.loading-flatpickr input').forEach(field => {
        field.style.paddingRight = '';
        
        // Keep hidden fields always hidden and disabled
        const hiddenFields = ['rentStartDate', 'rentEndDate', 'rentStartTime', 'rentEndTime', 
                             'eventStartDate', 'eventEndDate', 'eventStartTime', 'eventEndTime'];
        if (!field.name || !hiddenFields.includes(field.name)) {
            field.disabled = false;
        }
    });
}

/**
 * Get minimum day booking value from calendar elements
 * @returns {number} - Minimum days for booking
 */
export function getMinDayBooking() {
    const appointmentCalendarEl = document.querySelector('#fullCalendarAppointment');
    const hallBookingCalendarEl = document.querySelector('#fullCalendarGemengApp');

    let minDayBooking = 4; // Default for hall booking

    if (appointmentCalendarEl) {
        // For appointments, use J+3 or the value from data attribute  
        minDayBooking = parseInt(appointmentCalendarEl.dataset.mindaybooking || 3);
    } else if (hallBookingCalendarEl) {
        // For hall booking, use J+4 or the value from data attribute
        minDayBooking = parseInt(hallBookingCalendarEl.dataset.mindaybooking || 4);
    }
    
    return minDayBooking;
}

/**
 * Check if current context is an appointment form
 * @returns {boolean} - True if appointment form context
 */
export function isAppointmentForm() {
    return document.querySelector('.form-appointment') !== null ||
           document.querySelector('form.js-form-push') !== null;
}

/**
 * Check if current context is a booking form
 * @returns {boolean} - True if booking form context
 */
export function isBookingForm() {
    return document.querySelector('.form-bookingapp') !== null;
}

/**
 * Safely trigger an event on an element
 * @param {Element} element - Target element
 * @param {string} eventType - Event type (e.g., 'change', 'input')
 */
export function triggerEvent(element, eventType) {
    if (element) {
        element.dispatchEvent(new Event(eventType, { bubbles: true }));
    }
}

/**
 * Debounce function to limit function call frequency
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Group partial bookings by time continuity
 * @param {Array} partials - Array of partial booking objects
 * @returns {Array} - Array of grouped partial bookings
 */
export function groupPartialBookings(partials) {
    let grouped = [];
    let currentGroup = [];
    
    for (let i = 0; i < partials.length; i++) {
        const curr = partials[i];
        
        if (currentGroup.length === 0) {
            currentGroup.push(curr);
        } else {
            const prev = currentGroup[currentGroup.length - 1];
            const prevDate = parseDDMMYYYY(prev.date);
            const currDate = parseDDMMYYYY(curr.date);
            const prevEnd = new Date(`${prevDate.getFullYear()}-${(prevDate.getMonth() + 1).toString().padStart(2, '0')}-${prevDate.getDate().toString().padStart(2, '0')}T${prev.endTime}`);
            const currStart = new Date(`${currDate.getFullYear()}-${(currDate.getMonth() + 1).toString().padStart(2, '0')}-${currDate.getDate().toString().padStart(2, '0')}T${curr.startTime}`);
            
            const dayDiff = (currDate - prevDate) / (1000 * 60 * 60 * 24);
            let hourGap = null;
            
            if (dayDiff === 0) {
                hourGap = (currStart - prevEnd) / (1000 * 60 * 60);
            } else if (dayDiff === 1) {
                hourGap = (currStart - prevEnd) / (1000 * 60 * 60);
            }
            
            if (hourGap !== null && hourGap <= 1) {
                currentGroup.push(curr);
            } else {
                grouped.push([...currentGroup]);
                currentGroup = [curr];
            }
        }
    }
    
    if (currentGroup.length > 0) {
        grouped.push([...currentGroup]);
    }
    
    return grouped;
}

// Helper function for groupPartialBookings
function parseDDMMYYYY(dateStr) {
    const [dd, mm, yyyy] = dateStr.split('-');
    return new Date(`${yyyy}-${mm}-${dd}`);
}

/**
 * Initialize booking validation link with hash from localStorage
 * This function retrieves the booking hash from localStorage and appends it to the validation page link
 */
export function initBookingValidationLink() {
    const bookingLink = document.querySelector('.js-booking-link');

    if (!bookingLink) {
        return; // No booking link found on this page
    }

    // Try to get hash from localStorage (old booking system) or sessionStorage
    const hash = localStorage.getItem('uniqueHash') || sessionStorage.getItem('bookingHash');

    if (hash) {
        const baseUrl = bookingLink.dataset.baseUrl || bookingLink.href;

        // Ensure the URL ends with a slash before appending the hash
        const url = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
        bookingLink.href = url + hash;

        // console.log('Booking validation link updated with hash:', hash);
    } else {
        // console.warn('No booking hash found in storage');
    }
}